// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

/**
 * D-CRX-86 — the enrollment form's pregnancy radio. The server owns the mapping to screening answers
 * (PregnancyScreening.AnswersFor), and every choice answers both questions so the PDF ticks one box:
 * pregnant → UNIV-09 YES + UNIV-11 NA; breastfeeding → UNIV-09 NO + UNIV-11 YES;
 * unknown → UNIV-09 UNKNOWN + UNIV-11 UNKNOWN; na → UNIV-09 NA + UNIV-11 NA. This file only reads them back.
 */
export type PregnancyStatus = 'na' | 'pregnant' | 'breastfeeding' | 'unknown'

/** In the enrollment form's own order (§01: N/A, Pregnant, Breastfeeding, Unknown). */
export const PREGNANCY_OPTIONS: { value: PregnancyStatus; label: string }[] = [
  { value: 'na', label: 'N/A' },
  { value: 'pregnant', label: 'Pregnant' },
  { value: 'breastfeeding', label: 'Breastfeeding' },
  { value: 'unknown', label: 'Unknown' },
]

/** Plain words for the two screening questions the radio answers. */
const QUESTION: Record<string, string> = {
  'UNIV-09': 'the pregnancy question',
  'UNIV-11': 'the breastfeeding question',
}

const SKIP_REASON: Record<string, string> = {
  rule_not_published: 'is not set up at this pharmacy',
  screening_schema_missing: 'is not set up at this pharmacy (screening is not installed)',
  therapy_not_found: 'could not be saved because the enrollment was not found',
  answered_in_screening: 'was already answered on the screening tab — change it there',
}

type Skip = { rule_ref: string; reason: string }

const upperFirst = (t: string) => t.charAt(0).toUpperCase() + t.slice(1)

/**
 * The soft note for a save whose pregnancy answers were (partly) not recorded, else null. Plain words,
 * no rule codes. A failed write (edit path, rolled back) gets its own message: the rest of the
 * enrollment saved, only the pregnancy status did not.
 */
export function pregnancyNote(res: { skipped: Skip[] } | null | undefined): string | null {
  const s = res?.skipped ?? []
  if (s.length === 0) return null
  if (s.some(x => x.reason === 'write_failed'))
    return 'Saved, but the pregnancy status could not be recorded. Please try again, or answer it on the screening tab.'
  if (s.every(x => x.reason === 'answered_in_screening'))
    return `${upperFirst(s.map(x => QUESTION[x.rule_ref] ?? x.rule_ref).join(' and '))} ${s.length > 1 ? 'were' : 'was'} already answered on the screening tab, so the form left ${s.length > 1 ? 'them' : 'it'} as is — change ${s.length > 1 ? 'them' : 'it'} there.`
  const why = s.map(x => `${QUESTION[x.rule_ref] ?? x.rule_ref} ${SKIP_REASON[x.reason] ?? `was not recorded (${x.reason})`}`).join('; ')
  return `Saved, but the pregnancy status was not fully recorded: ${why}.`
}

/**
 * The radio's opening value from the therapy's current answers (UNIV-09, UNIV-11), '' when they say
 * nothing the radio can show. Order (board 2026-10-15): UNIV-09 YES → pregnant, UNIV-09 UNKNOWN →
 * unknown, UNIV-11 YES → breastfeeding, UNIV-09 NO / NA → na. The seed is display only: an untouched
 * radio is never sent, so it can never rewrite either answer.
 */
export function pregnancySeed(univ09: string | null | undefined, univ11: string | null | undefined): PregnancyStatus | '' {
  const p = (univ09 ?? '').trim().toUpperCase()
  const b = (univ11 ?? '').trim().toUpperCase()
  if (p === 'YES') return 'pregnant'
  if (p === 'UNKNOWN') return 'unknown'
  if (b === 'YES') return 'breastfeeding'
  if (p === 'NO' || p === 'NA') return 'na'
  return ''
}
